import { useCallback, useEffect, useState } from 'react';
import {
  Badge, Banner, Button, Card, DataTable, Form, FormLayout, InlineStack, Layout, Modal, Page,
  Select, Text, TextField, Tabs, BlockStack, ChoiceList,
} from '@shopify/polaris';
import { apiFetch } from '../api';

// Alta, verificación y pausa de las profesionales del consultorio.
//
// ── Dónde viven estos datos, y por qué hay un puente ──
// Las profesionales están en la base de `mobile-api`, junto a las usuarias de
// la app, sus citas y sus valoraciones. Meterlas en la base de este backend
// obligaría a duplicar la tabla o a cruzar dos bases en cada consulta.
//
// Esta página NO llama allí directamente. La primera versión sí lo hacía, con
// el token de servicio metido en el bundle vía `VITE_MOBILE_ADMIN_TOKEN`, y era
// un agujero: el SPA se sirve en `/panel/assets/*` sin comprobar ninguna
// sesión, así que cualquiera que abriera esa URL podía sacar el token del
// JavaScript y verificar profesionales. Ahora se llama a
// `/panel-api/consultorio/*` —autenticado con el session token de Shopify, como
// el resto del panel— y el token de `mobile-api` no sale del servidor.
//
// ── Verificar no es publicar ──
// Son dos interruptores distintos a propósito. «Verificada» es un hecho
// comprobado sobre su colegiación y no se retira porque esté de vacaciones; «en
// pausa» es que ahora no atiende. Mezclarlos obligaría a re-verificar a alguien
// cada vez que vuelve de un viaje.

type Professional = {
  id: string;
  email: string | null;
  display_name: string;
  headline: string;
  bio: string;
  specialty: string;
  license_number: string;
  license_authority: string;
  languages: string[];
  modalities: string[];
  duration_minutes: number;
  price_cents: number;
  currency: string;
  commission_bps: number;
  stripe_account_id: string | null;
  verified_at: string | null;
  paused_at: string | null;
  appointments: number;
  reviews: number;
  /** Devoluciones a la tarjeta que agotaron los reintentos: hay que revisarlas en Stripe. */
  failed_refunds?: number;
  /** Devoluciones que el servidor sigue reintentando solo. */
  pending_refunds?: number;
};

// `author_name`: con qué nombre firmó el comentario la autora (la ficha de la app
// lo enseña así; sin él, «Una usuaria»). Un mobile-api anterior no lo manda.
type Review = { id: string; rating: number; body: string; created_at: string; removed_at: string | null; display_name: string; author_name?: string | null };

async function panel<T>(path: string, init: RequestInit = {}): Promise<T> {
  return apiFetch<T>(`/panel-api/consultorio${path}`, init);
}

const EMPTY = {
  email: '', displayName: '', headline: '', bio: '', specialty: '',
  licenseNumber: '', licenseAuthority: '', modalities: ['texto'],
  durationMinutes: '30', priceCents: '3500', commissionBps: '1500', stripeAccountId: '',
};

export default function Professionals() {
  const [tab, setTab] = useState(0);
  const [professionals, setProfessionals] = useState<Professional[]>([]);
  const [reviews, setReviews] = useState<Review[]>([]);
  const [error, setError] = useState('');
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<string | null>(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      setError('');
      const [list, reviewList] = await Promise.all([
        panel<{ professionals: Professional[] }>('/profesionales'),
        panel<{ reviews: Review[] }>('/valoraciones'),
      ]);
      setProfessionals(list.professionals);
      setReviews(reviewList.reviews);
    } catch (problem) {
      setError((problem as Error).message);
    }
  }, []);
  useEffect(() => { void load(); }, [load]);

  const save = async () => {
    // Las mismas reglas que el servidor (`validPriceCents` y
    // `STRIPE_ACCOUNT_PATTERN`, mobile-api/src/consultorio.ts), dichas aquí
    // antes de enviar para no devolver un «invalid_request» a secas.
    const price = Number(form.priceCents);
    if (!Number.isInteger(price) || (price !== 0 && price < 50)) {
      setError('La tarifa tiene que ser 0 (consulta gratuita) o al menos 50 céntimos: Stripe no cobra menos de 0,50 €.');
      return;
    }
    if (form.stripeAccountId.trim() && !/^acct_[A-Za-z0-9]{6,}$/.test(form.stripeAccountId.trim())) {
      setError('La cuenta de Stripe Connect tiene que ser un identificador acct_… (cópialo del panel de Stripe).');
      return;
    }
    setSaving(true);
    try {
      const body = {
        email: form.email || undefined,
        displayName: form.displayName,
        headline: form.headline,
        bio: form.bio,
        specialty: form.specialty,
        licenseNumber: form.licenseNumber,
        licenseAuthority: form.licenseAuthority,
        languages: ['es'],
        modalities: form.modalities,
        durationMinutes: Number(form.durationMinutes),
        priceCents: Number(form.priceCents),
        commissionBps: Number(form.commissionBps),
        stripeAccountId: form.stripeAccountId.trim() || undefined,
      };
      if (editing) await panel(`/profesionales/${editing}`, { method: 'PUT', body: JSON.stringify(body) });
      else await panel('/profesionales', { method: 'POST', body: JSON.stringify(body) });
      setOpen(false); setEditing(null); setForm(EMPTY);
      await load();
    } catch (problem) {
      setError((problem as Error).message);
    } finally {
      setSaving(false);
    }
  };

  const toggle = async (id: string, path: 'verificar' | 'pausar', value: boolean) => {
    try {
      await panel(`/profesionales/${id}/${path}`, {
        method: 'POST',
        body: JSON.stringify(path === 'verificar' ? { verified: value } : { paused: value }),
      });
      await load();
    } catch (problem) { setError((problem as Error).message); }
  };

  const edit = (professional: Professional) => {
    setEditing(professional.id);
    setForm({
      email: professional.email ?? '',
      displayName: professional.display_name,
      headline: professional.headline,
      bio: professional.bio,
      specialty: professional.specialty,
      licenseNumber: professional.license_number,
      licenseAuthority: professional.license_authority,
      modalities: professional.modalities,
      durationMinutes: String(professional.duration_minutes),
      priceCents: String(professional.price_cents),
      commissionBps: String(professional.commission_bps),
      stripeAccountId: professional.stripe_account_id ?? '',
    });
    setOpen(true);
  };

  const rows = professionals.map(professional => [
    <BlockStack gap="050" key={professional.id}>
      <Text as="span" fontWeight="semibold">{professional.display_name}</Text>
      <Text as="span" tone="subdued">{professional.email ?? 'sin cuenta enlazada'}</Text>
    </BlockStack>,
    professional.specialty,
    `${professional.license_number} · ${professional.license_authority}`,
    `${(professional.price_cents / 100).toFixed(2)} ${professional.currency} · ${professional.duration_minutes} min`,
    professional.modalities.join(', '),
    <InlineStack gap="200" key={`${professional.id}-state`}>
      <Badge tone={professional.verified_at ? 'success' : 'attention'}>
        {professional.verified_at ? 'Verificada' : 'Sin verificar'}
      </Badge>
      {professional.paused_at ? <Badge tone="warning">En pausa</Badge> : null}
      {/* Una devolución que agotó los reintentos no se arregla sola: la
          paciente ve «estamos revisando tu devolución» hasta que alguien la
          resuelva en Stripe. */}
      {professional.failed_refunds ? <Badge tone="critical">{`${professional.failed_refunds} devolución(es) por revisar en Stripe`}</Badge> : null}
      {professional.pending_refunds ? <Badge tone="attention">{`${professional.pending_refunds} devolución(es) reintentándose`}</Badge> : null}
    </InlineStack>,
    `${professional.appointments} citas · ${professional.reviews} valoraciones`,
    <InlineStack gap="200" key={`${professional.id}-actions`}>
      <Button size="slim" onClick={() => edit(professional)}>Editar</Button>
      <Button size="slim" onClick={() => void toggle(professional.id, 'verificar', !professional.verified_at)}>
        {professional.verified_at ? 'Quitar verificación' : 'Verificar'}
      </Button>
      {/* Sin cuenta enlazada no se reanuda (el servidor lo rechaza): volvería al
          directorio una ficha sin nadie que atienda. */}
      <Button
        size="slim"
        disabled={Boolean(professional.paused_at) && !professional.email}
        accessibilityLabel={professional.paused_at && !professional.email ? 'Reanudar: enlaza antes su cuenta' : undefined}
        onClick={() => void toggle(professional.id, 'pausar', !professional.paused_at)}>
        {professional.paused_at ? (professional.email ? 'Reanudar' : 'Reanudar (sin cuenta)') : 'Pausar'}
      </Button>
    </InlineStack>,
  ]);

  const reviewRows = reviews.map(review => [
    review.display_name,
    `${review.rating}/5`,
    // Tal como se ve en la ficha de la app: el comentario y su firma.
    review.body ? `${review.body} — ${review.author_name || 'Una usuaria'}` : '—',
    new Date(review.created_at).toLocaleDateString('es-ES'),
    review.removed_at ? <Badge tone="critical" key={review.id}>Retirada</Badge> : <Badge key={review.id}>Visible</Badge>,
    <Button
      key={`${review.id}-action`}
      size="slim"
      onClick={async () => {
        try {
          await panel(`/valoraciones/${review.id}/retirar`, { method: 'POST', body: JSON.stringify({ removed: !review.removed_at }) });
          await load();
        } catch (problem) { setError((problem as Error).message); }
      }}>
      {review.removed_at ? 'Restaurar' : 'Retirar'}
    </Button>,
  ]);

  return (
    <Page
      title="Consultorio"
      subtitle="Profesionales verificadas, tarifas y valoraciones"
      primaryAction={{ content: 'Dar de alta', onAction: () => { setEditing(null); setForm(EMPTY); setOpen(true); } }}>
      <Layout>
        {error.includes('mobile_admin_token_missing') ? (
          <Layout.Section>
            <Banner tone="warning" title="Falta el token del servicio">
              <p>
                Define <code>MOBILE_ADMIN_TOKEN</code> en las variables de ESTE backend, con el mismo valor que{' '}
                <code>ADMIN_API_TOKEN</code> en mobile-api. Va en el servidor a propósito: si viajara al navegador,
                cualquiera que abriese el panel podría verificar profesionales.
              </p>
            </Banner>
          </Layout.Section>
        ) : null}
        {error ? (
          <Layout.Section>
            <Banner tone="critical" title="No se ha podido completar la operación"><p>{error}</p></Banner>
          </Layout.Section>
        ) : null}

        <Layout.Section>
          <Card padding="0">
            <Tabs
              tabs={[
                { id: 'pros', content: `Profesionales (${professionals.length})` },
                { id: 'reviews', content: `Valoraciones (${reviews.length})` },
              ]}
              selected={tab}
              onSelect={setTab}>
              {tab === 0 ? (
                <DataTable
                  columnContentTypes={['text', 'text', 'text', 'text', 'text', 'text', 'text', 'text']}
                  headings={['Profesional', 'Especialidad', 'Colegiación', 'Tarifa', 'Modalidades', 'Estado', 'Actividad', '']}
                  rows={rows}
                />
              ) : (
                <DataTable
                  columnContentTypes={['text', 'text', 'text', 'text', 'text', 'text']}
                  headings={['Profesional', 'Nota', 'Comentario', 'Fecha', 'Estado', '']}
                  rows={reviewRows}
                />
              )}
            </Tabs>
          </Card>
        </Layout.Section>

        <Layout.Section>
          <Banner tone="info" title="Cómo funciona el alta">
            <p>
              La profesional se registra primero en la app como una usuaria más. Aquí se la busca por su correo, se
              comprueba su número de colegiado contra el registro de su colegio y se pulsa «Verificar». Hasta ese
              momento no aparece en el directorio de la app.
            </p>
            <p>
              Sin cuenta de Stripe Connect se la puede dar de alta y verificar, pero sus citas no podrán cobrarse.
            </p>
          </Banner>
        </Layout.Section>
      </Layout>

      <Modal
        open={open}
        onClose={() => { setOpen(false); setEditing(null); }}
        title={editing ? 'Editar profesional' : 'Dar de alta una profesional'}
        primaryAction={{ content: editing ? 'Guardar' : 'Dar de alta', onAction: () => void save(), loading: saving }}
        secondaryActions={[{ content: 'Cancelar', onAction: () => { setOpen(false); setEditing(null); } }]}>
        <Modal.Section>
          <Form onSubmit={() => void save()}>
            <FormLayout>
              <TextField
                label="Correo de su cuenta en la app"
                helpText="Debe existir ya. Es lo que le da acceso a su agenda y a sus citas."
                type="email" autoComplete="off"
                value={form.email} onChange={value => setForm({ ...form, email: value })}
              />
              <FormLayout.Group>
                <TextField label="Nombre" autoComplete="off" value={form.displayName} onChange={value => setForm({ ...form, displayName: value })} />
                <TextField label="Especialidad" autoComplete="off" value={form.specialty} onChange={value => setForm({ ...form, specialty: value })} />
              </FormLayout.Group>
              <TextField label="Titular" autoComplete="off" helpText="Una línea. Es lo que se lee en el directorio." value={form.headline} onChange={value => setForm({ ...form, headline: value })} />
              <TextField label="Presentación" autoComplete="off" multiline={4} value={form.bio} onChange={value => setForm({ ...form, bio: value })} />
              <FormLayout.Group>
                <TextField label="Nº de colegiada" autoComplete="off" value={form.licenseNumber} onChange={value => setForm({ ...form, licenseNumber: value })} />
                <TextField label="Colegio" autoComplete="off" helpText="Comprobado contra su registro público." value={form.licenseAuthority} onChange={value => setForm({ ...form, licenseAuthority: value })} />
              </FormLayout.Group>
              <ChoiceList
                allowMultiple
                title="Modalidades que ofrece"
                choices={[
                  { label: 'Por escrito', value: 'texto' },
                  { label: 'Llamada de voz', value: 'voz' },
                  { label: 'Videoconsulta', value: 'video' },
                ]}
                selected={form.modalities}
                onChange={value => setForm({ ...form, modalities: value })}
              />
              <FormLayout.Group>
                <Select
                  label="Duración"
                  options={[15, 20, 30, 45, 60].map(value => ({ label: `${value} min`, value: String(value) }))}
                  value={form.durationMinutes}
                  onChange={value => setForm({ ...form, durationMinutes: value })}
                />
                <TextField
                  label="Tarifa (céntimos)" type="number" autoComplete="off"
                  helpText="3500 = 35,00 €. 0 = consulta gratuita; si no, mínimo 50 (0,50 €, lo mínimo que cobra Stripe)."
                  value={form.priceCents} onChange={value => setForm({ ...form, priceCents: value })}
                />
                <TextField
                  label="Comisión (bps)" type="number" autoComplete="off"
                  helpText="1500 = 15 %."
                  value={form.commissionBps} onChange={value => setForm({ ...form, commissionBps: value })}
                />
              </FormLayout.Group>
              <TextField
                label="Cuenta de Stripe Connect" autoComplete="off"
                helpText="acct_… Sin ella sus citas no se pueden cobrar."
                value={form.stripeAccountId} onChange={value => setForm({ ...form, stripeAccountId: value })}
              />
            </FormLayout>
          </Form>
        </Modal.Section>
      </Modal>
    </Page>
  );
}
